/**
 * 上へスクロールしたときだけヘッダーを出す
 *
 * @description 追従そのものはCSSの position: fixed が持つ。ここは「いま上下どちらへ動いたか」を
 *              クラスで伝えるだけ。下向きなら画面の上へ逃がし、上向きに戻したら降ろす。
 *
 *              トップだけはFVに大きいロゴが入るので、FVが画面から抜けきるまで
 *              ヘッダー側のロゴを伏せる。こちらは向きではなく位置で決まる。
 */

/** 逃がすクラス */
const HIDDEN = 'is-hidden';

/** FVに重なるあいだロゴを伏せるクラス */
const AT_FV = 'is-atFv';

/** 向きが変わったと見なす移動量(px)。1pxごとに反応すると細かな揺れで点滅する */
const THRESHOLD = 8;

/** この位置より上ではいつでも出す。ここで隠すと先頭へ戻ったときにヘッダーが無い状態になる */
const ALWAYS_VISIBLE = 120;

/** クリエイター一覧の面を見ているあいだ html に付く印（page-creators.ts が付ける） */
const HOLD = 'is-creatorExplore';

/** SPの境目。SCSSの mq() と同じ */
const SP = '(max-width: 1024px)';

/** 解除する手続き。非同期遷移でヘッダーごと作り直すときに使う */
let detach: (() => void) | null = null;

/**
 * 監視を止めて、逃がしていたヘッダーを元に戻す
 *
 * @returns {void}
 */
const destroyHeaderReveal = (): void => {
    detach?.();
    detach = null;
};

/**
 * ヘッダーの出し入れを始める
 *
 * @param scope 新しい器。非同期遷移の最中は新旧の器が揃って居るので、
 *              document から引くと出ていくほうのFVやヘッダーを掴んでしまう
 * @returns {void}
 */
const initHeaderReveal = (scope: ParentNode = document): void => {
    destroyHeaderReveal();

    const header = scope.querySelector<HTMLElement>('#header');

    if (!header) return;

    const logo = header.querySelector<HTMLElement>('.header__logo');
    // トップにしか無い。下層ページではロゴを伏せる判定そのものが要らない
    const fv = scope.querySelector<HTMLElement>('#mv');

    let last = Math.max(0, window.scrollY);
    let ticking = false;

    /**
     * いまのスクロール位置から出す/隠すを決める
     *
     * @returns {void}
     */
    const update = (): void => {
        const y = Math.max(0, window.scrollY);
        const diff = y - last;

        // FVが少しでも見えているあいだは伏せたまま。ヘッダーの出し入れとは別に、
        // 向きに関係なく今の位置だけで決める。FVが無いページでは必ず出す
        if (logo) {
            const atFv = !!fv && fv.getBoundingClientRect().bottom > 0;

            logo.classList.toggle(AT_FV, atFv);

            // 伏せている間は見えないしクリックもできないので、タブの送り順からも外す。
            // SPはFVに大きいロゴが無く、伏せずに出したままなので対象にしない
            if (atFv && !window.matchMedia(SP).matches) {
                logo.setAttribute('tabindex', '-1');
            } else {
                logo.removeAttribute('tabindex');
            }
        }

        // 面は画面いっぱいに出るので、裏でページが動いてもヘッダーは出したままにする
        if (y <= ALWAYS_VISIBLE || document.documentElement.classList.contains(HOLD)) {
            header.classList.remove(HIDDEN);
            last = y;
            return;
        }

        // 遷移後の位置合わせやアンカーのような大きな飛びは、指の動きではないので
        // 向きとしては扱わない。基準だけ取り直す
        if (Math.abs(diff) > window.innerHeight) {
            last = y;
            return;
        }

        if (Math.abs(diff) < THRESHOLD) return;

        header.classList.toggle(HIDDEN, diff > 0);
        last = y;
    };

    const onScroll = (): void => {
        if (ticking) return;

        ticking = true;
        requestAnimationFrame(() => {
            ticking = false;
            update();
        });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // FVの高さは画面サイズで変わる。スクロールしないまま幅を変えられても追従させる
    window.addEventListener('resize', onScroll);

    // 読み込み直後や遷移直後にページの途中へ着地することがあるので、一度いまの位置で決める
    update();

    detach = () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        header.classList.remove(HIDDEN);
    };
};

export { initHeaderReveal, destroyHeaderReveal };

document.addEventListener('DOMContentLoaded', () => initHeaderReveal());
